import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import MyApiClient from "../../../api-client/MyApiClient";

/**
 * Modal condiviso per la creazione "al volo" di un nuovo cliente durante una
 * vendita manuale (abbonamenti, corsi, lezioni, noleggio impianti).
 *
 * Props:
 *  - show:        boolean, visibilità del modal
 *  - onHide:      () => void, chiusura senza creazione
 *  - onCreated:   (player: { value: string, label: string }) => void
 *                 invocata a creazione avvenuta con l'utente da selezionare
 *  - initialName: string opzionale, prefilla nome/cognome dal testo cercato
 */
const CreateTemporaryPlayerModal = ({ show, onHide, onCreated, initialName = "" }) => {
    const emptyForm = { givenName: "", familyName: "", email: "", phoneNumber: "" };
    const [formData, setFormData] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // Prefilla nome/cognome dalla stringa cercata nell'AsyncSelect
    useEffect(() => {
        if (show) {
            setError("");
            setSubmitting(false);
            const parts = (initialName || "").trim().split(/\s+/);
            setFormData({
                ...emptyForm,
                givenName: parts[0] || "",
                familyName: parts.slice(1).join(" ") || "",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        if (!formData.givenName.trim()) return "Il nome è obbligatorio.";
        if (!formData.familyName.trim()) return "Il cognome è obbligatorio.";
        if (!formData.email.trim()) return "L'email è obbligatoria.";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) return "Formato email non valido.";
        return "";
    };

    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError("");
        setSubmitting(true);

        try {
            const tokenDetailsString = sessionStorage.getItem("userDetails");
            if (!tokenDetailsString) {
                setError("Sessione non valida. Effettua nuovamente l'accesso.");
                setSubmitting(false);
                return;
            }
            const token = JSON.parse(tokenDetailsString).idToken;
            const apiClient = new MyApiClient();

            const response = await apiClient.createTemporaryPlayer(token, {
                givenName: formData.givenName.trim(),
                familyName: formData.familyName.trim(),
                email: formData.email.trim().toLowerCase(),
                phoneNumber: formData.phoneNumber.trim() || undefined,
            });

            if (response && response.error) {
                setError(response.error.detail || "Errore durante la creazione del cliente.");
                setSubmitting(false);
                return;
            }

            const userId = response?.data?.userId;
            if (!userId) {
                setError("Risposta non valida dal server.");
                setSubmitting(false);
                return;
            }

            onCreated({
                value: userId,
                label: `${formData.givenName.trim()} ${formData.familyName.trim()}`,
            });
        } catch (err) {
            console.error("Errore createTemporaryPlayer:", err);
            setError("Errore imprevisto durante la creazione del cliente.");
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={submitting ? undefined : onHide} centered>
            <Modal.Header closeButton={!submitting}>
                <Modal.Title>Crea nuovo cliente</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Il cliente verrà creato come utente SportID e riceverà via email le
                    credenziali per accedere e completare il proprio profilo.
                </p>
                <Form noValidate>
                    <Row>
                        <Col className="col-lg-6">
                            <Form.Label>Nome *</Form.Label>
                            <Form.Control
                                name="givenName"
                                value={formData.givenName}
                                onChange={handleChange}
                                placeholder="Nome"
                                disabled={submitting}
                            />
                        </Col>
                        <Col className="col-lg-6">
                            <Form.Label>Cognome *</Form.Label>
                            <Form.Control
                                name="familyName"
                                value={formData.familyName}
                                onChange={handleChange}
                                placeholder="Cognome"
                                disabled={submitting}
                            />
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col className="col-lg-12">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="email@esempio.it"
                                disabled={submitting}
                            />
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col className="col-lg-12">
                            <Form.Label>Telefono (facoltativo)</Form.Label>
                            <Form.Control
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                placeholder="Telefono"
                                disabled={submitting}
                            />
                        </Col>
                    </Row>
                    {error && <p className="text-danger mt-3 mb-0">{error}</p>}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide} disabled={submitting}>
                    Annulla
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Creazione...
                        </>
                    ) : (
                        "Crea e seleziona"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateTemporaryPlayerModal;
